import React, { FC } from 'react';
import {
  PrimaryNavigation,
  PrimaryNavigationItem,
  PrimaryNavigationExpansionItem,
  PrimaryNavigationSubItem,
} from '@lifeomic/chroma-react/components/PrimaryNavigation';
import sidebarNavItems from './data/navItems';
import {
  useActiveAccount,
  useActiveProjectId,
  usePolicy,
  useFeatures,
  useABAC,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import EnforceABAC, {
  permissions,
} from '@lifeomic/phc-web-toolkit/src/components/EnforceABAC';
import { useIntl } from 'react-intl';
import urls from '../../../util/urls';
import { tiles, testIds as tilesTestIds } from '../../../common/tiles';
import messages from './data/messages';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useSelector } from 'react-redux';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { customAppsSelector } from '../../../redux/selectors/customAppsSelector';
import CirclesIcon from '@lifeomic/phc-web-toolkit/src/components/Icons/Circles';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { JupyterNotebooks } from '../icons/JupyterNotebooks';
import { featuresSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/features';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';
import {
  BookOpen,
  Code as AppsIcon,
  Home,
  ShoppingBag,
} from '@lifeomic/chromicons';
import getIsInFedEnvironment from '@lifeomic/phc-web-toolkit/src/util/getIsInFedEnvironment';
import { Marketplace } from '@lifeomic/phc-web-toolkit/src/components/Icons';
import EnforceAccountFeatureToggle from '@lifeomic/phc-web-toolkit/src/components/EnforceAccountFeatureToggle';
import { lifeAccount } from '@lifeomic/phc-web-toolkit/src/redux/util/constants';
import useIsMobile from '@lifeomic/phc-web-toolkit/src/hooks/useIsMobile';

const useStyles = makeStyles(theme => ({
  gapLine: {
    height: 1,
    background: theme.palette.black[100],
    margin: theme.spacing(2, -2),
    padding: 0,
  },
}));

export const testIds = {
  omicsRoot: 'sidebarNav-omicsRoot',
  filesRoot: 'sidebarNav-filesRoot',
  automationRoot: 'sidebarNav-automationRoot',
  ontologiesRoot: 'sidebarNav-ontologiesRoot',
  notebooks: 'sidebarNav-notebooks',
  alertsRoot: 'sidebarNav-alerts',
};

const EnforceActiveProject: FC = ({ children }: any) => {
  const activeProjectId = useActiveProjectId();

  return activeProjectId ? children : null;
};

export const SidebarNavComponent: FC = () => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const activeAccount = useActiveAccount();
  const activeProjectId = useActiveProjectId();
  const policy = usePolicy();
  const features = useSelector(featuresSelectors.selectFeatures);
  const customApps: Array<Record<string, unknown>> =
    useSelector(customAppsSelector);
  const hasFeature = useFeatures();
  const [enforce] = useABAC();
  const isMobile = useIsMobile();

  // Collapse sidebar navigation if user is on mobile
  React.useEffect(() => {
    const isSidebarCollapsed = JSON.parse(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      window.localStorage.getItem('@@lifeomic/store/layoutSidebarCollapsed'),
    );

    if (isMobile && !isSidebarCollapsed) {
      window.localStorage.setItem(
        '@@lifeomic/store/layoutSidebarCollapsed',
        'true',
      );
    }
  }, [isMobile]);

  const filteredSidebarItems = sidebarNavItems.filter(n =>
    n.item.shouldDisplay(activeAccount, policy, features),
  );

  return (
    <PrimaryNavigation>
      {features?.projectLandingPage && activeProjectId && (
        <PrimaryNavigationItem
          to={urls.app.projectLandingPage(activeProjectId)}
          icon={<Home />}
          label={formatMessage(messages.home)}
          key={-1}
        />
      )}
      {filteredSidebarItems.map((n, i) => {
        const Icon = n.item.navIcon;

        if (n.item.id === 'omics') {
          return (
            <EnforceActiveProject key={i}>
              <PrimaryNavigationExpansionItem
                icon={<Icon />}
                to={n.item.uri}
                label={n.item.title}
                rootParentPath={urls.app.omics.root()}
                {...getTestProps(testIds.omicsRoot)}
              >
                {tiles.genomicsIngestions.shouldDisplay(
                  activeAccount,
                  policy,
                  features,
                ) && (
                  <PrimaryNavigationSubItem
                    to={tiles.genomicsIngestions.uri}
                    label={tiles.genomicsIngestions.title}
                    {...getTestProps(tilesTestIds.genomicsIngestions)}
                  />
                )}
                <PrimaryNavigationSubItem
                  to={tiles.genomicsTests.uri}
                  label={tiles.genomicsTests.title}
                  {...getTestProps(tilesTestIds.genomicsTests)}
                />
                {tiles.genomicsActions.shouldDisplay(activeAccount, policy) && (
                  <PrimaryNavigationSubItem
                    to={tiles.genomicsActions.uri}
                    label={tiles.genomicsActions.title}
                    {...getTestProps(tilesTestIds.genomicsActions)}
                  />
                )}
              </PrimaryNavigationExpansionItem>
            </EnforceActiveProject>
          );
        } else if (n.item.id === 'files') {
          return (
            <EnforceActiveProject key={i}>
              <PrimaryNavigationExpansionItem
                icon={<Icon />}
                to={n.item.uri}
                label={n.item.title}
                rootParentPath={urls.app.filesRoot()}
                {...getTestProps(testIds.filesRoot)}
              >
                {!getIsInFedEnvironment() && (
                  <EnforceABAC
                    orPermissions={[
                      permissions.projectAdmin,
                      permissions.fileAdmin,
                    ]}
                  >
                    <PrimaryNavigationSubItem
                      to={tiles.sftp.uri}
                      label={tiles.sftp.title}
                      {...getTestProps(tilesTestIds.sftp)}
                    />
                  </EnforceABAC>
                )}
                {features?.fileTransfer && !getIsInFedEnvironment() && (
                  <EnforceABAC
                    orPermissions={[
                      permissions.projectAdmin,
                      permissions.fileAdmin,
                    ]}
                  >
                    <PrimaryNavigationSubItem
                      to={tiles.fileTransfer.uri}
                      label={tiles.fileTransfer.title}
                      {...getTestProps(tilesTestIds.fileTransfer)}
                    />
                  </EnforceABAC>
                )}
              </PrimaryNavigationExpansionItem>
            </EnforceActiveProject>
          );
        } else if (n.item.id === 'ontologies') {
          return (
            <EnforceActiveProject key={i}>
              <EnforceAccountFeatureToggle accountFeature="ontologies">
                <PrimaryNavigationExpansionItem
                  icon={<Icon />}
                  to={n.item.uri}
                  label={n.item.title}
                  rootParentPath={urls.app.ontologies.root()}
                  {...getTestProps(testIds.ontologiesRoot)}
                >
                  <PrimaryNavigationSubItem
                    to={tiles.ontologiesMarketplace.uri}
                    label={tiles.ontologiesMarketplace.title}
                    {...getTestProps(tilesTestIds.ontologiesMarketplace)}
                  />
                </PrimaryNavigationExpansionItem>
              </EnforceAccountFeatureToggle>
            </EnforceActiveProject>
          );
        } else if (n.item.id === 'workflows') {
          return (
            <EnforceActiveProject key={i}>
              <PrimaryNavigationExpansionItem
                icon={<Icon />}
                to={n.item.uri}
                label={n.item.title}
                rootParentPath={urls.app.workflows.root()}
                {...getTestProps(tilesTestIds.workflows)}
              >
                <PrimaryNavigationSubItem
                  to={tiles.onboardingSnapshot.uri}
                  label={tiles.onboardingSnapshot.title}
                  {...getTestProps(tilesTestIds.onboardingSnapshot)}
                />
                {tiles.fileValidation.shouldDisplay(
                  activeAccount,
                  policy,
                  features,
                ) && (
                  <PrimaryNavigationSubItem
                    to={tiles.fileValidation.uri}
                    label={tiles.fileValidation.title}
                    {...getTestProps(tilesTestIds.fileValidation)}
                  />
                )}
                {tiles.regenerativeMedicineOps.shouldDisplay(
                  activeAccount,
                  policy,
                  features,
                ) && (
                  <PrimaryNavigationSubItem
                    to={tiles.regenerativeMedicineOps.uri}
                    label={tiles.regenerativeMedicineOps.title}
                    {...getTestProps(tilesTestIds.regenerativeMedicineOps)}
                  />
                )}
                {tiles.reportTemplates.shouldDisplay(activeAccount) && (
                  <PrimaryNavigationSubItem
                    to={tiles.reportTemplates.uri}
                    label={tiles.reportTemplates.title}
                    {...getTestProps(tilesTestIds.reportTemplates)}
                  />
                )}
                {tiles.zoomAppointments.shouldDisplay(activeAccount) && (
                  <PrimaryNavigationSubItem
                    to={tiles.zoomAppointments.uri}
                    label={tiles.zoomAppointments.title}
                    {...getTestProps(tilesTestIds.zoomAppointments)}
                  />
                )}
                {tiles.aiRules.shouldDisplay(activeAccount) && (
                  <PrimaryNavigationSubItem
                    to={tiles.aiRules.uri}
                    label={tiles.aiRules.title}
                    {...getTestProps(tilesTestIds.aiRules)}
                  />
                )}
                {tiles.zoriChat.shouldDisplay(
                  activeAccount,
                  policy,
                  features,
                ) && (
                  <PrimaryNavigationSubItem
                    to={tiles.zoriChat.uri}
                    label={tiles.zoriChat.title}
                    {...getTestProps(tilesTestIds.zoriChat)}
                  />
                )}
              </PrimaryNavigationExpansionItem>
            </EnforceActiveProject>
          );
        }

        return !!n.item.requiresProject && !activeProjectId ? null : (
          <PrimaryNavigationItem
            key={i}
            to={n.item.uri}
            icon={<Icon />}
            label={n.item.title}
            {...getTestProps(n.item.testId)}
          />
        );
      })}
      {filteredSidebarItems.length > 0 && <li className={classes.gapLine}></li>}
      <EnforceActiveProject>
        <EnforceAccountFeatureToggle accountFeature="circles">
          {(enforce(permissions.accessAdmin) ||
            activeAccount?.id === lifeAccount) && (
            <PrimaryNavigationItem
              to={tiles.circles.uri}
              icon={<CirclesIcon />}
              label={tiles.circles.title}
              {...getTestProps(tilesTestIds.circles)}
            />
          )}
        </EnforceAccountFeatureToggle>
      </EnforceActiveProject>
      <EnforceActiveProject>
        <EnforceAccountFeatureToggle accountFeature="marketplace">
          {hasFeature('marketplaceEmbedded') && !getIsInFedEnvironment() && (
            <PrimaryNavigationItem
              to={urls.app.marketplace.embedded()}
              icon={<Marketplace />}
              label={formatMessage(messages.marketplace)}
              betaLabelText={formatMessage(messages.betaLabel)}
              beta
              {...getTestProps(tilesTestIds.manageAppStore)}
            />
          )}
        </EnforceAccountFeatureToggle>
      </EnforceActiveProject>
      <EnforceActiveProject>
        <EnforceAccountFeatureToggle accountFeature="circles">
          {customApps.length > 0 && (
            <PrimaryNavigationItem
              to={tiles.customApps.uri}
              icon={<AppsIcon />}
              label={tiles.customApps.title}
              {...getTestProps(tilesTestIds.customApps)}
            />
          )}
        </EnforceAccountFeatureToggle>
      </EnforceActiveProject>
      <EnforceAccountFeatureToggle accountFeature="notebooks">
        {activeAccount &&
          !getIsInFedEnvironment() &&
          activeAccount.type === 'ENTERPRISE' &&
          activeAccount.products?.includes('Notebooks') && (
            <PrimaryNavigationItem
              to={urls.jupyterNotebooks()}
              icon={<JupyterNotebooks />}
              label={formatMessage(messages.notebooksLabel)}
              {...getTestProps(testIds.notebooks)}
            />
          )}
      </EnforceAccountFeatureToggle>
      {tiles.reseller.shouldDisplay(activeAccount, policy) && (
        <EnforceABAC andPermissions={[permissions.ruleAdmin]}>
          <PrimaryNavigationItem
            to={tiles.reseller.uri}
            icon={<ShoppingBag />}
            label={tiles.reseller.title}
          />
        </EnforceABAC>
      )}
      <PrimaryNavigationItem
        to={tiles.userGuide.uri.url}
        icon={<BookOpen />}
        label={tiles.userGuide.title}
      />
    </PrimaryNavigation>
  );
};

SidebarNavComponent.displayName = 'SidebarNavComponent';
export default SidebarNavComponent;
